import React, { useState, useCallback, useEffect, useRef } from "react";
import { ImageOff } from "lucide-react";

// Rewrite known CDN URLs to small, cache-friendly WebP so lists load fast
// and images sharpen quickly instead of staying stuck as blur placeholders.
function optimize(src, width) {
  if (!src || typeof src !== "string" || !width) return src;
  if (src.includes("images.unsplash.com")) {
    const base = src.split("?")[0];
    return `${base}?w=${width}&q=70&fm=webp&auto=format&fit=crop`;
  }
  return src;
}

/**
 * Resilient image: requests a resized WebP, blur-up on load, retries once on
 * error, then a graceful icon fallback. Never leaves a permanent blur.
 */
export function SmartImage({ src, alt = "", width = 400, priority = false, className = "", imgClassName = "", fallback: Fallback = ImageOff, fallbackClassName = "" }) {
  const optimized = optimize(src, width);
  const [status, setStatus] = useState("loading"); // loading | loaded | error
  const [tries, setTries] = useState(0);
  const [uri, setUri] = useState(optimized);
  const imgRef = useRef(null);

  useEffect(() => { setUri(optimize(src, width)); setStatus("loading"); setTries(0); }, [src, width]);

  // handle already-cached images (onLoad may not fire) + hard anti-stuck timeout
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) { setStatus("loaded"); return undefined; }
    const t = setTimeout(() => setStatus((s) => (s === "loading" ? "loaded" : s)), 6000);
    return () => clearTimeout(t);
  }, [uri]);

  const onError = useCallback(() => {
    if (tries === 0 && optimized) { setUri(optimize(src, width)); setTries(1); }        // retry optimized
    else if (tries === 1) { setUri(src); setTries(2); }                                  // fall back to original
    else setStatus("error");
  }, [src, width, optimized, tries]);

  return (
    <div className={`relative overflow-hidden bg-[#e7ecdf] ${className}`}>
      {status !== "error" ? (
        <img
          ref={imgRef} src={uri} alt={alt}
          loading={priority ? "eager" : "lazy"} decoding="async" fetchPriority={priority ? "high" : "auto"}
          onLoad={() => setStatus("loaded")} onError={onError}
          className={`h-full w-full object-cover img-blur-up ${status === "loaded" ? "img-loaded" : ""} ${imgClassName}`}
        />
      ) : (
        <div className={`h-full w-full flex items-center justify-center bg-gradient-to-br from-[#3f9142] to-[#173f1e] ${fallbackClassName}`}>
          <Fallback className="h-8 w-8 text-white/85" />
        </div>
      )}
    </div>
  );
}

export default SmartImage;
